import { $isCodeNode } from "@lexical/code-core";
import { $isLinkNode } from "@lexical/link";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $addUpdateTag,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  BLUR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  defineExtension,
  HISTORY_PUSH_TAG,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  safeCast,
  type LexicalEditor,
  type LexicalExtension,
  type LexicalNode,
} from "lexical";
import type { CapabilityCommand } from "../../core/capabilities";
import type { SuggestionProvider } from "../../core/suggestions";
import type { DocumentId } from "../../core/types";
import { $runCommand } from "../../lexical/commands";
import { $insertToken } from "../tokens/insert";
import { $isTokenNode } from "../tokens/node";
import { createSuggestionEngine, type ActiveMatch, type SuggestionEngine } from "./engine";
import { SLASH_TRIGGER, slashProvider } from "./slash";
import { findTrigger } from "./trigger";

export interface SuggestionsConfig {
  providers: readonly SuggestionProvider[];
  documentId: DocumentId;
  debounceMs: number;
  /** See `SuggestionEngineOptions.cacheContext`. */
  cacheContext: () => string;
  /**
   * The commands this preset offers. With `slash` on, they are the rows of the built-in `/` menu;
   * either way they are what a row's `commandId` may name.
   */
  commands: readonly CapabilityCommand[];
  slash: boolean;
}

/** By type: the table nodes load on demand, and this extension is part of every editor. */
const $isTableCell = (node: LexicalNode): boolean => node.getType() === "tablecell";

export interface SuggestionsOutput {
  engine: SuggestionEngine;
  /** Accepts the row at `index`, or the active row. Does nothing if the document has moved on. */
  accept(index?: number): boolean;
  /** The same for the active row, from inside an update (a key command). */
  $accept(): boolean;
}

/**
 * The trigger the caret is in, read from the current state. Nothing here is a trigger: a range
 * selection, IME composition, code (a block or the inline format), a link, or a token.
 */
function $activeMatch(editor: LexicalEditor, engine: SuggestionEngine): ActiveMatch | null {
  if (editor.isComposing()) return null;
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const node = selection.anchor.getNode();
  if (!$isTextNode(node) || $isTokenNode(node) || node.hasFormat("code")) return null;
  const excluded = $findMatchingParent(
    node,
    (parent) => $isCodeNode(parent) || $isLinkNode(parent),
  );
  if (excluded !== null) return null;
  const before = node.getTextContent().slice(0, selection.anchor.offset);
  const match = findTrigger(before, engine.rules, node.getPreviousSibling() === null);
  if (match === null) return null;
  // Everything the slash menu makes is a block, and a table cell holds paragraphs only. References
  // (`@`, `[[`) work in a cell like anywhere else.
  if (match.trigger === SLASH_TRIGGER && $findMatchingParent(node, $isTableCell) !== null) {
    return null;
  }
  return { ...match, id: `${node.getKey()}:${match.start}` };
}

/**
 * Watches the caret for a trigger, feeds the search engine, and owns the keys while its menu is
 * open. It never updates the editor from a listener: the only write is an accepted suggestion.
 */
export const SuggestionsExtension: LexicalExtension<
  SuggestionsConfig,
  "z-editor/suggestions",
  SuggestionsOutput,
  unknown
> = defineExtension({
  name: "z-editor/suggestions",
  config: safeCast<SuggestionsConfig>({
    providers: [],
    documentId: "",
    debounceMs: 120,
    cacheContext: () => "",
    commands: [],
    slash: false,
  }),
  build(editor, config): SuggestionsOutput {
    const engine = createSuggestionEngine({
      ...config,
      providers: config.slash
        ? [...config.providers, slashProvider(config.commands)]
        : config.providers,
    });
    /** Must run inside an update. Key commands already are in one; a click opens its own. */
    const $accept = (): boolean => {
      const chosen = engine.current();
      if (chosen === null) return false;
      // The search may have taken a while. If the text, the caret, or the query is no longer what
      // the menu was showing results for, replacing it would destroy something else.
      const now = $activeMatch(editor, engine);
      const selection = $getSelection();
      const node = $getNodeByKey(chosen.match.id.slice(0, chosen.match.id.lastIndexOf(":")));
      const current =
        now !== null && now.id === chosen.match.id && now.query === chosen.match.query;
      if (!current || !$isRangeSelection(selection) || !$isTextNode(node)) {
        engine.update(null);
        return false;
      }
      const { item } = chosen;
      const command =
        "commandId" in item
          ? config.commands.find((candidate) => candidate.id === item.commandId)
          : undefined;
      if ("commandId" in item && command === undefined) {
        engine.update(null);
        return false;
      }
      $addUpdateTag(HISTORY_PUSH_TAG);
      const typed = node.select(now.start, selection.anchor.offset);
      if ("token" in item) return $insertToken(item.token).ok;
      // The typed "/query" goes, and what is left of the block is converted, in the same update.
      typed.removeText();
      return command !== undefined && $runCommand(command);
    };
    const accept = (index?: number): boolean => {
      if (index !== undefined) engine.setActive(index);
      let accepted = false;
      editor.update(() => void (accepted = $accept()), { discrete: true });
      return accepted;
    };
    return { engine, accept, $accept };
  },
  register(editor, _config, state) {
    const { engine, $accept } = state.getOutput();
    if (engine.rules.length === 0) return () => {};

    /** While rows are showing, these keys belong to the menu and reach nothing below it. */
    const whenOpen = (handle: () => void) => (event: KeyboardEvent | null) => {
      const { open, items } = engine.getState();
      if (!open || items.length === 0) return false;
      if (event !== null && (event.isComposing || event.keyCode === 229)) return false;
      event?.preventDefault();
      handle();
      return true;
    };

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        engine.update(editorState.read(() => $activeMatch(editor, engine)));
      }),
      editor.registerCommand(
        BLUR_COMMAND,
        () => (engine.update(null), false),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ARROW_DOWN_COMMAND,
        whenOpen(() => engine.move(1)),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        whenOpen(() => engine.move(-1)),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        whenOpen(() => void $accept()),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_TAB_COMMAND,
        whenOpen(() => void $accept()),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (event) => {
          if (!engine.getState().open) return false;
          event.preventDefault();
          engine.dismiss();
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      () => engine.dispose(),
    );
  },
});
